import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { userActivityAPI } from '../services/api';

// Map route paths to screen names
const getScreenName = (pathname: string): string => {
  // Remove leading slash and split
  const parts = pathname.split('/').filter(Boolean);
  
  if (parts.length === 0) return 'Dashboard';
  
  // Map common routes to readable names
  const routeMap: Record<string, string> = {
    'firms': 'Firms',
    'projects': 'Projects',
    'cases': 'Cases',
    'profile': 'Profile',
    'vendor-invoices': 'Vendor Invoices',
    'client-invoices': 'Client Invoices',
    'pending-estimates': 'Pending Estimates',
    'pending-client-estimates': 'Pending Client Estimates',
    'tds-payments': 'TDS Payments',
    'admin/agreements': 'Admin Agreements',
    'masquerade-users': 'Masquerade Users',
    'user-management': 'User Management',
    'email-settings': 'Email Settings',
    'system-settings': 'System Settings',
    'email-logs': 'Email Logs',
    'backend-logs': 'Backend Logs',
    'user-activity-logs': 'User Activity Logs',
    'legal-section': 'Legal Section',
    'reports/user-time': 'User Time Report',
    'reports/user-time-sheet': 'User Time Sheet',
    'reports/vendor-assignment': 'Vendor Assignment Report',
    'reports/time-tracker': 'Time Tracker Report',
  };

  // Check for exact match first
  const fullPath = parts.join('/');
  if (routeMap[fullPath]) {
    return routeMap[fullPath];
  }

  // Check for partial matches (e.g., firms/:firmId -> Firms)
  for (const [route, name] of Object.entries(routeMap)) {
    if (fullPath.startsWith(route)) {
      // If there are additional parts, append them
      const remaining = parts.slice(route.split('/').length);
      if (remaining.length > 0) {
        // Handle specific views
        if (remaining[0] === 'new') {
          return `${name} - Create`;
        }
        if (remaining[0] === 'edit' || remaining[remaining.length - 1] === 'edit') {
          return `${name} - Edit`;
        }
        // For view pages, try to get a descriptive name
        const lastPart = remaining[remaining.length - 1];
        if (!isNaN(Number(lastPart))) {
          // It's an ID, use the parent name
          return `${name} - View`;
        }
        return `${name} - ${lastPart.charAt(0).toUpperCase() + lastPart.slice(1)}`;
      }
      return name;
    }
  }

  // Fallback: capitalize first letter of each part
  return parts.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' - ');
};

export const useScreenTracking = () => {
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    // Only track if user is authenticated
    if (!user) return;

    // Don't track login/register pages
    if (location.pathname === '/login' || location.pathname === '/register') {
      return;
    }

    // Get screen name from route
    const screenName = getScreenName(location.pathname);

    // Extract metadata from pathname (e.g., case_id, project_id, firm_id)
    const metadata: any = {};
    const pathParts = location.pathname.split('/').filter(Boolean);
    
    // Look for ID patterns in the path
    pathParts.forEach((part, index) => {
      if (part === 'firms' && pathParts[index + 1] && !isNaN(Number(pathParts[index + 1]))) {
        metadata.firm_id = parseInt(pathParts[index + 1]);
      }
      if (part === 'cases' && pathParts[index + 1] && !isNaN(Number(pathParts[index + 1]))) {
        metadata.case_id = parseInt(pathParts[index + 1]);
      }
      if (part === 'projects' && pathParts[index + 1] && !isNaN(Number(pathParts[index + 1]))) {
        metadata.project_id = parseInt(pathParts[index + 1]);
      }
      if (part === 'vendor-invoices' && pathParts[index + 1] && !isNaN(Number(pathParts[index + 1]))) {
        metadata.invoice_id = parseInt(pathParts[index + 1]);
      }
      if (part === 'client-invoices' && pathParts[index + 1] && !isNaN(Number(pathParts[index + 1]))) {
        metadata.invoice_id = parseInt(pathParts[index + 1]);
      }
    });

    // Log screen view (fire and forget - don't wait for response)
    userActivityAPI.logScreenView(screenName, Object.keys(metadata).length > 0 ? metadata : undefined)
      .catch(error => {
        // Silently fail - don't break the app if tracking fails
        console.debug('Failed to log screen view:', error);
      });
  }, [location.pathname, user]);
};

